import { useWorkspaceStatistics } from '@hooks/getQueries';
import useWorkspace from '@hooks/useWorkspace';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  alpha,
  Box,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { BarChart3, Bot, ClipboardList, MessageSquare, Mic, Sparkles, Users } from 'lucide-react';
import { useState } from 'react';

/**
 * Mini stat card with icon and value
 */
function StatCard({ icon: Icon, label, value, subValue, color, trend, isLoading, helpText }) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 2,
        }}
      >
        <Skeleton variant="circular" width={40} height={40} />
        <Box flex={1}>
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={24} />
        </Box>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(color || theme.palette.primary.main, 0.3),
          boxShadow: `0 4px 12px ${alpha(color || theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          backgroundColor: alpha(color || theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={22} color={color || theme.palette.primary.main} />
      </Box>
      <Box flex={1} minWidth={0}>
        {helpText ? (
          <Tooltip title={helpText} arrow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
              }}
            >
              <Box
                component="span"
                sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled' }}
              >
                {label}
              </Box>
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
        )}
        <Box display="flex" alignItems="baseline" gap={0.5}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
            {value.toLocaleString()}
          </Typography>
          {subValue && (
            <Typography variant="caption" color="text.secondary">
              {subValue}
            </Typography>
          )}
          {trend !== undefined && trend > 0 && (
            <Tooltip title={`+${trend} in last 7 days`} arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 0.5,
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.75,
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                }}
              >
                <TrendingUpIcon
                  sx={{ fontSize: 12, color: theme.palette.success.main, mr: 0.25 }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontSize: 10, fontWeight: 600, color: theme.palette.success.main }}
                >
                  +{trend}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * Breakdown stat showing distribution
 */
function BreakdownCard({ title, icon: Icon, items, total, color, isLoading }) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          height: '100%',
        }}
      >
        <Skeleton width="50%" height={20} sx={{ mb: 2 }} />
        <Box display="flex" flexDirection="column" gap={1}>
          <Skeleton height={24} />
          <Skeleton height={24} />
        </Box>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Icon size={16} color={color || theme.palette.primary.main} />
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Total: {total.toLocaleString()}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        {items.map((item, idx) => (
          <Box key={idx} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: item.color || theme.palette.grey[400],
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {item.value.toLocaleString()}
            </Typography>
            {total > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ width: 40 }}>
                {Math.round((item.value / total) * 100)}%
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default function StatisticsPanel() {
  const theme = useTheme();
  const { activeWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: stats, isLoading, isFetching } = useWorkspaceStatistics(activeWorkspaceId);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({
      queryKey: ['workspace-statistics', activeWorkspaceId],
    });
    // Small delay for visual feedback
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const studies = stats?.studies || {};
  const responses = stats?.responses || {};
  const aiParticipants = stats?.ai_participants || {};
  const recentActivity = stats?.recent_activity || {};

  return (
    <Box px={{ xs: 2, md: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <BarChart3 size={18} color={theme.palette.primary.main} />
          <Typography variant="subtitle2">Workspace Overview</Typography>
        </Box>
        <Tooltip title="Refresh statistics" arrow>
          <span>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isFetching}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              }}
            >
              {isRefreshing || isFetching ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2}>
        {/* Row 1: Primary Stats */}
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={ClipboardList}
            label="Total Studies"
            value={studies.total || 0}
            subValue={`${studies.active_total || 0} active`}
            helpText="Total number of studies in this workspace (interviews + surveys)."
            color={theme.palette.primary.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={MessageSquare}
            label="Total Responses"
            value={responses.total || 0}
            trend={recentActivity.last_7_days_total}
            helpText="Total responses collected across all studies (human + AI)."
            color={theme.palette.success.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={Bot}
            label="AI Participants"
            value={aiParticipants.total || 0}
            helpText="Total AI participants configured in this workspace."
            color={theme.palette.secondary.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={Sparkles}
            label="AI Responses"
            value={(responses.ai_interview_responses || 0) + (responses.ai_survey_responses || 0)}
            helpText="Total responses generated by AI participants across studies."
            color={theme.palette.info.main}
            isLoading={isLoading}
          />
        </Grid>

        {/* Row 2: Breakdowns */}
        <Grid item xs={12} sm={6} md={4}>
          <BreakdownCard
            title="Studies Breakdown"
            icon={ClipboardList}
            total={studies.total || 0}
            color={theme.palette.primary.main}
            isLoading={isLoading}
            items={[
              {
                label: 'Interviews',
                value: studies.interviews || 0,
                color: theme.palette.info.main,
              },
              {
                label: 'Surveys',
                value: studies.surveys || 0,
                color: theme.palette.success.main,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <BreakdownCard
            title="Interview Responses"
            icon={Mic}
            total={responses.interview_responses || 0}
            color={theme.palette.info.main}
            isLoading={isLoading}
            items={[
              {
                label: 'Human',
                value: responses.human_interview_responses || 0,
                color: theme.palette.primary.main,
              },
              {
                label: 'AI Generated',
                value: responses.ai_interview_responses || 0,
                color: theme.palette.secondary.main,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <BreakdownCard
            title="Survey Responses"
            icon={Users}
            total={responses.survey_responses || 0}
            color={theme.palette.success.main}
            isLoading={isLoading}
            items={[
              {
                label: 'Human',
                value: responses.human_survey_responses || 0,
                color: theme.palette.primary.main,
              },
              {
                label: 'AI Generated',
                value: responses.ai_survey_responses || 0,
                color: theme.palette.secondary.main,
              },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
